<script lang="ts" setup>
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import dayjs from "dayjs";
import type { VForm } from "vuetify/components";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import TimezoneSelect from "@/components/selects/TimezoneSelect.vue";
import FileUploadingGrid from "@/views/trips/components/FileUploadingGrid.vue";
import { API } from "@/api/api";
import { useBaseView, useDateTime } from "@/composables";
import { useError } from "@/composables/useError";
import { useValidation } from "@/composables/useValidation";
import { OrderFreightType, OrderStopType } from "@/constants/order";
import type { RouteStopDto } from "@/api/trips/dto/trip.dto";
import type { StopInfoFormProps } from "@/types/props";
import type { PickupInfoFormData, StopInfoFreight, TripRouteFreight, TripRouteStop } from "@/types/trip";

const loading = defineModel<boolean>("loading", { default: false });

const props = withDefaults(defineProps<StopInfoFormProps>(), {
  preload: false,
  readonly: false,
});

const emits = defineEmits<{
  (e: "save"): void;
}>();

const api = new API();

const { dateToUTC, timezonesShortList } = useDateTime();
const { getPreviewPath, uniqueArray } = useBaseView();
const { requiredValidator, digitsValidator } = useValidation();
const { showAPIError, scrollToError } = useError();

const formRef = useTemplateRef<VForm | null>("form");
const loadData = ref<PickupInfoFormData>({
  bolNumbers: [""],
  actualFreights: [],
  files: [],
  date: "",
  time: "",
  timezone: "",
});

const routeStops = computed<(TripRouteStop | RouteStopDto)[]>(() => props.route.routeStops);

const freights = computed<TripRouteFreight[]>(
  () =>
    uniqueArray(
      props.route.freights.filter((f) => props.stop.freights.includes(f.id)),
      ["id"],
    ) as TripRouteFreight[],
);

const stopFreights = computed<TripRouteFreight[]>(() =>
  props.stop.freights.map((sf) => freights.value.find((f) => f.id === sf)).filter((f) => f !== undefined),
);

const actualPalletsQuantity = computed(() =>
  loadData.value.actualFreights.reduce(
    (sum, f) => (f.type === OrderFreightType.pallets ? sum + toInt(f.quantity) : sum),
    0,
  ),
);

const palletsQuantity = computed(() =>
  freights.value.reduce((sum, f) => (f.type === OrderFreightType.pallets ? sum + toInt(f.plannedQuantity) : sum), 0),
);

const actualPiecesQuantity = computed(() =>
  loadData.value.actualFreights.reduce(
    (sum, f) => (f.type === OrderFreightType.pieces ? sum + toInt(f.quantity) : sum),
    0,
  ),
);

const piecesQuantity = computed(() =>
  freights.value.reduce((sum, f) => (f.type === OrderFreightType.pieces ? sum + toInt(f.plannedQuantity) : sum), 0),
);

const actualFreightsWeight = computed(() => loadData.value.actualFreights.reduce((sum, f) => sum + toInt(f.weight), 0));

const freightsWeight = computed(() => freights.value.reduce((sum, f) => sum + toInt(f.plannedWeight), 0));

const actualPalletsQuantityClass = computed(() =>
  getComparisonClass(actualPalletsQuantity.value, palletsQuantity.value),
);

const actualPiecesQuantityClass = computed(() => getComparisonClass(actualPiecesQuantity.value, piecesQuantity.value));

const actualFreightsWeightClass = computed(() => getComparisonClass(actualFreightsWeight.value, freightsWeight.value));

const freightsQuantity = computed(() => {
  const result = [];
  if (palletsQuantity.value > 0) {
    result.push(palletsQuantity.value + " plt");
  }
  if (piecesQuantity.value > 0) {
    result.push(piecesQuantity.value + " pcs");
  }
  return result.join(", ");
});

const canRemoveBolNumber = computed(() => loadData.value.bolNumbers.length > 1);

function toInt(value: string | number | null | undefined): number {
  return parseInt(String(value)) || 0;
}

function getComparisonClass(actual: number, planned: number): string {
  if (actual > planned) {
    return "text-error";
  } else if (actual < planned) {
    return "text-warning";
  } else {
    return "text-success";
  }
}

function addBolNumber(): void {
  loadData.value.bolNumbers.push("");
}

function removeBolNumber(index: number): void {
  loadData.value.bolNumbers.splice(index, 1);
}

function getFreightDeliveryPoint(freight: StopInfoFreight): string | null {
  const facility = routeStops.value.find(
    (o) => o.type === OrderStopType.delivery && o.freights.includes(freight.id),
  )?.facility;
  return facility ? facility.addressLine + ", " + facility.address : null;
}

function getFreightData(freight: StopInfoFreight): string {
  return freight.plannedQuantity + " " + getFreightType(freight) + ", " + freight.plannedWeight + " lbs";
}

function getFreightType(freight: StopInfoFreight): string {
  if (freight.type === OrderFreightType.pallets) return "plt";
  return "pcs";
}

function setBolNumberAutomatically(): void {
  const bolNumbers = loadData.value.bolNumbers;

  if (loadData.value.files.length) {
    loadData.value.files = loadData.value.files.map((file, index) => {
      return {
        ...file,
        bol: bolNumbers.length === 1 ? bolNumbers[0] : bolNumbers[index],
      };
    });
  }

  loadData.value.actualFreights = loadData.value.actualFreights.map((freight, index) => {
    return {
      ...freight,
      bolNumber: bolNumbers.length === 1 ? bolNumbers[0] : bolNumbers[index],
    };
  });
}

async function init(): Promise<void> {
  let freightNumber = 1;
  loadData.value.actualFreights = stopFreights.value.map((f) => ({
    ...f,
    weight: f.weight || f.plannedWeight,
    quantity: f.quantity || f.plannedQuantity,
    number: freightNumber++,
  }));

  loadData.value.bolNumbers = props.preload
    ? Array.from(new Set(stopFreights.value.map((f) => f.bolNumber ?? "")))
    : Array(loadData.value.actualFreights.length).fill("");

  if (!props.preload) {
    loadData.value.timezone = props.stop.facility?.timezone ?? "";
    const date = dayjs().tz(loadData.value.timezone);
    loadData.value.date = date.format("YYYY-MM-DD");
    loadData.value.time = date.format("HH:mm");

    return;
  }

  loadData.value.timezone = props.stop.timezone ?? "";
  const date = dayjs(props.stop.loadUnloadTime).tz(loadData.value.timezone);
  loadData.value.date = date.format("YYYY-MM-DD");
  loadData.value.time = date.format("HH:mm");

  try {
    const response = await api.trips.getTripFiles(props.tripId);

    if (response.success && response.data) {
      loadData.value.files = response.data
        .filter((f) => f.tags?.includes("route_stop:" + props.stop.id))
        .map((f) => ({
          ...f,
          previewPath: getPreviewPath(f),
          bol: f.tags?.find((t) => t.startsWith("bol:"))?.split(":")[1],
        }));
    }
  } catch (error) {
    console.error(error);
  }
}

async function uploadFiles(): Promise<void> {
  const uploadPromises = loadData.value.files.map(async (uploadingFile) => {
    uploadingFile.loading = true;

    try {
      const response = await api.trips.uploadRouteStopFile(
        props.tripId,
        String(props.stop.id),
        "bol",
        uploadingFile.file!,
        uploadingFile.bol,
      );

      if (response.success && response.data) {
        Object.assign(uploadingFile, {
          ...response.data,
          previewPath: getPreviewPath(response.data),
        });
      } else {
        uploadingFile.error = true;
      }
    } catch (error) {
      console.error(error);
      uploadingFile.error = true;
    } finally {
      uploadingFile.loading = false;
    }
  });

  await Promise.all(uploadPromises);
}

async function save(): Promise<void> {
  const result = await formRef.value?.validate();

  if (!result?.valid || loadData.value.files.length === 0) {
    scrollToError(formRef.value?.$el);

    return;
  }

  loading.value = true;

  await uploadFiles();

  try {
    const response = await api.trips.loadRouteStop(props.tripId, String(props.stop.id), {
      bols: loadData.value.bolNumbers.map((number) => ({
        number,
        files: loadData.value.files.filter((file) => file.bol === number),
      })),
      loadTime: dateToUTC(loadData.value.date + " " + loadData.value.time, loadData.value.timezone),
      freights: loadData.value.actualFreights,
    });

    if (response.success) {
      emits("save");
    } else {
      showAPIError(response, "Error load verification at stop");
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}

watch(() => loadData.value.bolNumbers, setBolNumberAutomatically, { deep: true });

onMounted(() => {
  init();
});

defineExpose({ save });
</script>

<template>
  <v-form ref="form">
    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-6 text-grey-navy">BOL info</div>
      <div v-for="(_, i) in loadData.bolNumbers" :key="i" class="mb-4 d-flex">
        <v-text-field
          v-model="loadData.bolNumbers[i]"
          :disabled="readonly"
          :rules="[requiredValidator]"
          autocomplete="off"
          class="required"
          color="primary"
          data-qa="bol-number-field"
          density="compact"
          hide-details="auto"
          label="BOL number"
          variant="outlined"
        />
        <v-btn
          v-if="canRemoveBolNumber && !readonly"
          class="ml-2"
          color="error"
          data-qa="remove-bol-button"
          icon="mdi-delete-outline"
          size="small"
          variant="text"
          @click="removeBolNumber(i)"
        ></v-btn>
      </div>
      <v-btn
        v-if="!readonly"
        class="font-size-11 font-weight-600 text-uppercase"
        color="primary"
        data-qa="add-bol-button"
        density="compact"
        prepend-icon="mdi-plus"
        variant="text"
        @click="addBolNumber"
      >
        Add BOL
      </v-btn>
    </div>
    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Files<span class="text-error">*</span></div>
      <file-uploading-grid v-model="loadData.files" :disabled="readonly" required @load="setBolNumberAutomatically">
        <template #footer="{ file }">
          <omni-select
            v-if="file"
            v-model="file.bol"
            :disabled="readonly"
            :items="loadData.bolNumbers"
            :rules="[requiredValidator]"
            class="mt-5"
            data-qa="bol-select-file-zone"
            label="BOL"
            required
          ></omni-select>
        </template>
      </file-uploading-grid>
    </div>
    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Freights at stop:</div>
      <div v-for="actualFreight in loadData.actualFreights" :key="actualFreight.id">
        <div class="bg-grey-lighten-4 pa-4">
          <div class="text-grey-darken-3 font-weight-500 mb-2">Freight#{{ actualFreight.number }}:</div>
          <div class="d-flex align-center justify-space-between">
            <div>
              <span class="text-grey-darken-2 font-weight-500">Planned: </span>
              <span class="text-grey-darken-3 font-weight-500" data-qa="freight-block-planned"
                >#{{ actualFreight.number }}: {{ getFreightData(actualFreight) }}</span
              >
            </div>
            <div class="d-flex align-center text-grey-darken-1">
              <v-icon class="mr-2" color="error" icon="mdi-map-marker-radius-outline" size="18"></v-icon>
              #{{ actualFreight.number }}: {{ getFreightDeliveryPoint(actualFreight) }}
            </div>
          </div>
        </div>
        <div class="bg-grey-lighten-5 pa-4">
          <div class="text-grey-darken-3 font-weight-500 mb-3">Actual:</div>
          <v-row>
            <v-col>
              <v-text-field
                v-model="actualFreight.quantity"
                :disabled="readonly"
                :rules="[requiredValidator, digitsValidator]"
                autocomplete="off"
                class="required bg-white"
                color="primary"
                data-qa="quantity-field"
                density="compact"
                hide-details="auto"
                label="Quantity"
                variant="outlined"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="actualFreight.weight"
                :disabled="readonly"
                :rules="[requiredValidator, digitsValidator]"
                autocomplete="off"
                class="required bg-white"
                color="primary"
                data-qa="total-weight-field"
                density="compact"
                hide-details="auto"
                label="Total weight"
                variant="outlined"
              />
            </v-col>
            <v-col>
              <omni-select
                v-model="actualFreight.bolNumber"
                :disabled="readonly"
                :items="loadData.bolNumbers"
                :rules="[requiredValidator]"
                class="bg-white"
                data-qa="actual-freight-bol-select"
                label="BOL"
                required
              ></omni-select>
            </v-col>
            <v-col>
              <v-text-field
                v-model="actualFreight.sealNumber"
                :disabled="readonly"
                autocomplete="off"
                class="bg-white"
                color="primary"
                data-qa="seal-number-field"
                density="compact"
                hide-details="auto"
                label="Seal number"
                variant="outlined"
              />
            </v-col>
          </v-row>
        </div>
      </div>
      <div>
        <div class="text-grey-darken-3 font-weight-500 mt-4 mb-2 text-right" data-qa="total-planned-freigth">
          Planned total: {{ freightsQuantity }}, {{ freightsWeight }} lbs
        </div>
        <div class="font-weight-500 text-grey-darken-3 text-right" data-qa="total-freight">
          Total:
          <span :class="actualPalletsQuantityClass">{{ actualPalletsQuantity }}</span> plt,
          <span :class="actualPiecesQuantityClass">{{ actualPiecesQuantity }}</span> pcs,
          <span :class="actualFreightsWeightClass">{{ actualFreightsWeight }}</span> lbs
        </div>
      </div>
    </div>
    <div>
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Time</div>
      <v-row>
        <v-col cols="3">
          <timezone-select
            v-model="loadData.timezone"
            :disabled="readonly"
            :items="timezonesShortList"
            :rules="[requiredValidator]"
            required
          />
        </v-col>
        <v-col cols="6">
          <date-picker v-model="loadData.date" :disabled="readonly" label="Date" required></date-picker>
        </v-col>
        <v-col cols="3">
          <time-picker v-model="loadData.time" :disabled="readonly" label="Time" required></time-picker>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<style lang="scss" scoped></style>
